/**
 * Was die Wiedergabe beim Warten mitschreibt (7.69o).
 *
 * Die Regel steht im gemeinsamen Teil (`pufferBedarf`); hier stehen die
 * Zahlen dazu: wie oft angehalten wurde, wie lange insgesamt, und ob eine
 * Wartezeit abgelaufen ist, statt fertig zu werden. Sie gehen mit dem Befund
 * an den Server -- eine Garantie, die man nicht messen kann, ist ein
 * Versprechen.
 */
export interface PufferBericht {
  /** Wie oft die Wiedergabe auf Daten gewartet hat. */
  haltungen: number;
  /** Wie lange insgesamt, in Millisekunden. */
  msGesamt: number;
  /** Die längste einzelne Wartezeit. */
  msMax: number;
  /**
   * Die *kürzeste* einzelne Wartezeit (7.143). Der Wächter hat einen Boden
   * von einer Viertelsekunde -- was darunter liegt, hat angehalten und im
   * selben Takt wieder angefangen, und genau das fühlt sich wie ein Fehler
   * an. Ohne diese Zahl liess sich ein Befund mit zwei Haltungen nicht
   * beurteilen: zwei lange Wartezeiten sind eine langsame Leitung, zwei
   * kurze sind ein Stottern.
   */
  msMin: number;
  /**
   * Der kürzeste Abstand zwischen dem Ende einer Wartezeit und dem Anfang
   * der nächsten (7.143), oder 0 bei höchstens einer.
   *
   * Das ist die Zahl zu Saschas „drückt ganz oft Pause und Play“: nach einer
   * Wartezeit läuft es erst weiter, wenn *alle* wieder Vorrat haben (7.69r).
   * Hält es fünfzig Millisekunden später wieder an, hat diese Regel nicht
   * getragen -- und das ist eine Aussage über die App, nicht über die
   * Maschine, denn der Vorrat war ja gerade da.
   */
  msLuecke: number;
  /** Wie oft die Wartezeit abgelaufen ist und es trotzdem weiterlief. */
  abgelaufen: number;
  /** Wird gerade gewartet? */
  laeuft: boolean;
  /**
   * **Warum** angehalten und warum weitergelaufen wurde, die letzten paar
   * Male (26. September, Bau #323). Dort stand: zweimal gewartet, je 410 ms,
   * 210 ms dazwischen -- also frei beim ersten möglichen Takt und wieder
   * angehalten beim nächsten. Welches Element, mit welchem Ladezustand und
   * welchem Vorsprung, sagte nichts. Jetzt steht es hier: `halt` mit dem
   * Element, dem es fehlte, `weiter` mit dem, was als satt galt.
   */
  verlauf: string[];
}

/**
 * Mehr als so viele Haltungen in einer Wiedergabe heissen: es hilft nicht.
 * Danach läuft es durch, sonst wird aus der Garantie ein Stottern.
 */
export const HALTUNGEN_MAX = 40;

const stand: PufferBericht = { haltungen: 0, msGesamt: 0, msMax: 0, msMin: 0, msLuecke: 0, abgelaufen: 0, laeuft: false, verlauf: [] };

/** So viele Einträge behält der Verlauf -- genug für ein Messfenster. */
const VERLAUF_MAX = 8;

function merke(eintrag: string): void {
  stand.verlauf.push(eintrag);
  if (stand.verlauf.length > VERLAUF_MAX) stand.verlauf.splice(0, stand.verlauf.length - VERLAUF_MAX);
}

/** Wann die letzte Wartezeit endete -- für die Lücke bis zur nächsten. */
let zuletztEnde: number | null = null;

export function pufferBeginnt(grund = ''): void {
  if (zuletztEnde !== null) {
    const luecke = Math.round(performance.now() - zuletztEnde);
    stand.msLuecke = stand.msLuecke === 0 ? luecke : Math.min(stand.msLuecke, luecke);
  }
  stand.haltungen += 1;
  stand.laeuft = true;
  merke(`halt ${grund}`.trim());
}

export function pufferEndet(dauerMs: number, abgelaufen: boolean, grund = ''): void {
  const ms = Math.round(dauerMs);
  merke(`weiter nach ${ms} ms${abgelaufen ? ' (abgelaufen)' : ''} ${grund}`.trim());
  stand.msGesamt += ms;
  stand.msMax = Math.max(stand.msMax, ms);
  stand.msMin = stand.msMin === 0 ? ms : Math.min(stand.msMin, ms);
  if (abgelaufen) stand.abgelaufen += 1;
  stand.laeuft = false;
  zuletztEnde = performance.now();
}

export function pufferGenug(): boolean {
  return stand.haltungen >= HALTUNGEN_MAX;
}

export function pufferBericht(): PufferBericht {
  return { ...stand, verlauf: [...stand.verlauf] };
}

/**
 * Und das andere Warten (7.69q): nicht auf *Daten*, sondern auf das erste
 * **Bild**. Sascha, 10. September: „ganz oft wenn ich play drücke, hält es
 * kurz vor den videos kurz an.“ Das ist genau dieser Fall -- das Element hat
 * `play()` bekommen, aber noch kein Bild, und der Abspielkopf wartet an
 * seiner Stelle, statt davonzulaufen. Beides fühlt sich gleich an und hat
 * verschiedene Ursachen; nur getrennt gezählt sagt ein Gerätebefund, welche
 * von beiden es war.
 */
export interface BildWarten {
  haltungen: number;
  msGesamt: number;
  msMax: number;
  /** Wie oft die anderthalb Sekunden abgelaufen sind und der Kopf weiterlief. */
  abgelaufen: number;
}

const aufsBild: BildWarten = { haltungen: 0, msGesamt: 0, msMax: 0, abgelaufen: 0 };

export function bildWartenBeginnt(): void {
  aufsBild.haltungen += 1;
}

export function bildWartenEndet(dauerMs: number, abgelaufen: boolean): void {
  aufsBild.msGesamt += Math.round(dauerMs);
  aufsBild.msMax = Math.max(aufsBild.msMax, Math.round(dauerMs));
  if (abgelaufen) aufsBild.abgelaufen += 1;
}

export function bildWartenBericht(): BildWarten {
  return { ...aufsBild };
}

/**
 * Die Zähler auf null -- für eine Messung, die nur den *laufenden* Betrieb
 * meint (7.78).
 *
 * Beim Anlauf darf gewartet werden: da sind die Daten wirklich noch nicht da.
 * Die Behauptung „mit geladenen Daten wird nicht gewartet“ gilt für die Zeit
 * danach, und wer sie prüfen will, muss den Anlauf abziehen können. Auf dem
 * Bauserver ist genau daran ein Lauf gescheitert, während hier alles stimmte.
 */
export function pufferZuruecksetzen(): void {
  aufsBild.haltungen = 0;
  aufsBild.msGesamt = 0;
  aufsBild.msMax = 0;
  aufsBild.abgelaufen = 0;
  stand.haltungen = 0;
  stand.msGesamt = 0;
  stand.msMax = 0;
  stand.msMin = 0;
  stand.msLuecke = 0;
  stand.abgelaufen = 0;
  stand.laeuft = false;
  stand.verlauf = [];
  // Und die Uhr der Lücke mit: sonst zählt die erste Wartezeit des neuen
  // Fensters einen Abstand zu einer, die nicht mehr mitgezählt wird.
  zuletztEnde = null;
}

declare global {
  interface Window {
    __puffer?: () => string;
    __pufferZurueck?: () => void;
  }
}
if (typeof window !== 'undefined') {
  window.__puffer = () => JSON.stringify({ ...pufferBericht(), aufsBild: bildWartenBericht() });
  window.__pufferZurueck = () => pufferZuruecksetzen();
}
